import React, {useCallback, useRef, useState} from "react";
import {ReactComponent as ImagePersonAddIcon} from "@assets/icons/image-person-add.svg";
import {useAppMetadata} from "../../core/hooks/use-app-metadata";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {ImageCropperModalData} from "../../core/components/image-cropper-modal";
import useModalWithData from "@common/hooks/use-modal-with-data";

const ProfileImageSection: React.FC = () => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const {uploadWidgetImage, widgetImage} = useAppMetadata();
    const {showModal} = useModalWithData<ImageCropperModalData>(ModalIds.IMAGE_CROPPER);

    const displayImage = previewUrl || widgetImage?.base64 || null;
    const MAX_SIZE_MB = 5;
    const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp", "image/gif"];

    const validateFile = (file: File): string | null => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            return "Nieprawidłowy typ pliku. Dozwolone są wyłącznie obrazy (PNG, JPG, WEBP, GIF).";
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            return `Plik jest za duży. Maksymalny rozmiar to ${MAX_SIZE_MB}MB.`;
        }
        return null;
    };

    const handleCropComplete = useCallback(async (croppedBlob: Blob) => {
        setIsUploading(true);

        const reader = new FileReader();
        reader.onload = (e) => setPreviewUrl(e.target?.result as string);
        reader.readAsDataURL(croppedBlob);

        try {
            const croppedFile = new File([croppedBlob], 'cropped-image.jpg', {type: 'image/jpeg'});
            await uploadWidgetImage(croppedFile);
            setPreviewUrl(null);
        } catch (error) {
            setError("Błąd podczas przesyłania zdjęcia. Spróbuj ponownie.");
            setPreviewUrl(null);
        } finally {
            setIsUploading(false);
        }
    }, [uploadWidgetImage]);

    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError(null);
        const reader = new FileReader();
        reader.onload = (e) => {
            const imageUrl = e.target?.result as string;
            showModal({
                imageUrl,
                onCropComplete: handleCropComplete,
                cropShape: "rect"
            });
        };
        reader.readAsDataURL(file);

        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [handleCropComplete, showModal]);

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="text-lg font-semibold text-on-surface">Twoje zdjęcie</h2>
                    <p className="text-base text-on-surface/80 leading-5 font-normal">
                        Wybierz zdjęcie, które będzie wyświetlane w widżecie zamiast standardowego obrazka.
                    </p>
                </div>

                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="mt-2 inline-flex flex-col items-center justify-center gap-3 w-[148px] h-[148px] rounded-[24px] bg-[#EEF1F2] hover:bg-[#E4E8EE] transition-colors overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {displayImage ? (
                            <img src={displayImage} alt="Zdjęcie aplikacji"
                                 className="w-full h-full object-cover"/>
                        ) : (
                            <div className="mt-4 flex flex-col gap-3">
                                <ImagePersonAddIcon className="h-5 w-5 text-black mx-auto"/>
                                <span
                                    className="text-lg font-semibold text-on-surface leading-4 text-center flex flex-col gap-1">
                                    <span>Dodaj</span>
                                    <span>zdjęcie</span>
                                </span>
                                <span className="text-[12px] text-on-surface/60 leading-4">
                                    PNG, JPG do 5MB
                                </span>
                            </div>
                        )}
                    </button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_TYPES.join(",")}
                        className="hidden"
                        onChange={handleFileChange}
                    />

                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>
            </div>
        </>
    );
};

export default ProfileImageSection;
